import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Container, Form, FormGroup, Label, Input, Alert, Card, CardBody, UncontrolledTooltip } from 'reactstrap';
import useGetConnectionQuery from './../Requests/useGetConnectionQuery';
import useAcceptConnection from './../Requests/useAcceptConnection';
import useDeclineConnection from './../Requests/useDeclineConnection';
import RegisteredSiteDto from '../Common/dtos/RegisteredSiteDto';
import useDisplayMessage from '../Common/useDisplayMessage';
import KaAlert from '../Common/KaAlert';
import BusinessAdminView from '../Common/BusinessAdminView';
import useGetAdminSites from '../Requests/useGetAdminSites';
import MultiQueryView from '../Common/MultiQueryView';
import { KaSelect, KaSelectOption, toKaSelectOptions } from '../Common/KaSelect';
import BoldButton from '../Common/BoldButton';
import SpinnerButton from '../Common/SpinnerButton';
import { UserContext } from '../UserAccess/UserContext';

const AcceptConnectionContent = ({ code }: { code: string }) => {
  const { refreshUser } = useContext(UserContext);
  const navigate = useNavigate();
  const displayMessage = useDisplayMessage();

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [acknowledgmentChecked, setAcknowledgmentChecked] = useState(false);

  const connectionRequest = useGetConnectionQuery(code);
  const sitesQuery = useGetAdminSites();

  const siteOptions = toKaSelectOptions(sitesQuery.data ?? [], 'id', 'name', { sortByLabel: true });

  const acceptConnection = useAcceptConnection(code, {
    onSuccess: () => {
      const selectedSite = sitesQuery.data?.find((site: RegisteredSiteDto) => site.id === selectedSiteId);
      const message = `Successfully connected ${selectedSite?.name} with ${connectionRequest.data?.sourceBusinessName}'s ${connectionRequest.data?.sourceSiteName}`;
      navigate('/dataSharing', { state: { successMessage: message } });
      refreshUser();
    },
    onError: (error) => {
      displayMessage.fail(`Failed to accept connection: ${error.message}`);
    }
  });

  const declineConnection = useDeclineConnection(code, {
    onSuccess: () => {
      navigate('/dataSharing', { state: { successMessage: 'Connection request declined' } });
    },
    onError: (error) => {
      displayMessage.fail(`Failed to decline connection: ${error.message}`);
    }
  });

  return (
    <Container>
      <MultiQueryView
        queries={{ request: connectionRequest, sites: sitesQuery }}
        renderData={({ request, sites }) => <>
          <h2 className="mb-2 ka-blue">Accept Data Connection</h2>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <Card>
            <CardBody>
              <Form>
                <div className="row mb-4 align-items-center justify-content-center">
                  <div className="col-lg-4 col-md-5">
                    <FormGroup>
                      <Label>Partner's Site</Label>
                      <div className="border rounded px-3 py-2">
                        <span>{request.sourceBusinessName}</span>
                        <span className="ms-2 text-muted">({request.sourceSiteName})</span>
                      </div>
                    </FormGroup>
                  </div>
                  <div className="col-auto px-2 mt-3">
                    <i className="bi bi-link-45deg fs-2 text-muted"></i>
                  </div>
                  <div className="col-lg-4 col-md-5">
                    <FormGroup>
                      <Label for="site-select">Your Site</Label>
                      <KaSelect
                        id="site-select"
                        value={sites.length > 0 ? siteOptions.find(opt => opt.value === selectedSiteId) : null}
                        onChange={(selected) => setSelectedSiteId((selected as KaSelectOption)?.value || '')}
                        options={siteOptions}
                        placeholder="Select a site..."
                        isDisabled={sites.length === 0}
                        isClearable={false}
                      />
                    </FormGroup>
                  </div>
                </div>
                <p className="text-muted mb-4">
                  By accepting, I agree to receive the selected site data from my partner. I'm
                  responsible for linking our product lists to access inventory and transaction data.
                </p>
                <div className="mb-5">
                  <h5 className="mb-3">Data Being Connected:</h5>
                  <div className="ms-10">
                    <div className="text-muted mb-2">Product Name</div>
                    <div className="text-muted mb-2">Product Allocation Dashboard</div>
                    <div className="text-muted mb-2">Order Transactions</div>
                  </div>
                </div>

                <div className="mb-4 border rounded bg-soft-secondary px-3 py-2">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      checked={acknowledgmentChecked}
                      onChange={(e) => setAcknowledgmentChecked(e.target.checked)}
                      className="form-check-input fs-3"
                      id="acknowledgment-checkbox"
                    />
                    <Label check for="acknowledgment-checkbox" className="form-check-label">
                      <span className="text-muted">
                        I acknowledge and accept this connection and the data being shared with me. I understand that access has been
                        granted by the other party at their discretion, and I am responsible for handling the data appropriately.
                      </span>
                      <div className="text-muted mt-2">
                        I agree to manage this connection in accordance with all applicable data privacy and security policies. Kahler
                        Automation is not responsible for how I use or manage the shared data, and I take full responsibility for ensuring
                        compliance with relevant regulations and internal policies.
                      </div>
                    </Label>
                  </FormGroup>
                  
                  <p className="text-muted small fst-italic mb-0">
                    I understand that any data shared while this connection is active will remain accessible in both my account and the
                    partner's account. If the connection is terminated, no new data will be exchanged. I am responsible for managing
                    when access is granted or revoked and for protecting the integrity of shared information at all times.
                  </p>
                </div>

                <div className="d-flex justify-content-between">
                  <BoldButton
                    className="btn-ghost-danger"
                    onClick={() => declineConnection.request()}
                    disabled={declineConnection.isLoading || acceptConnection.isLoading}
                  >
                    Decline
                  </BoldButton>
                  <span id="accept-button-wrapper">
                    <SpinnerButton
                      spinning={acceptConnection.isLoading}
                      className="btn-ghost-primary"
                      onClick={() => acceptConnection.request({ acceptingSiteId: selectedSiteId })}
                      disabled={
                        !acknowledgmentChecked ||
                        !selectedSiteId ||
                        sites.length === 0 ||
                        declineConnection.isLoading ||
                        acceptConnection.isLoading
                      }
                    >
                      Accept
                    </SpinnerButton>
                    {!acknowledgmentChecked && selectedSiteId && sites.length > 0 && (
                      <UncontrolledTooltip placement="top" target="accept-button-wrapper">
                        Please acknowledge the terms by checking the checkbox above and selecting a site
                      </UncontrolledTooltip>
                    )}
                  </span>
                </div>
              </Form>
            </CardBody>
          </Card>
        </>}
        renderError={err =>
          <Alert color="danger">
            Unable to load connection request: {err.message}
          </Alert>
        }
      />
    </Container>
  );
};

export const AcceptConnectionPage = () => {
  const { code } = useParams<{ code: string }>();

  if (!code) {
    return (
      <Container className="mt-5">
        <Alert color="danger">
          Invalid connection link. No code provided.
        </Alert>
      </Container>
    );
  }

  return (
    <BusinessAdminView
      render={() => <AcceptConnectionContent code={code} />}
    />
  );
};